import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Eye, Edit, CheckCircle, XCircle, Clock, Star, X, ChevronLeft, ChevronRight, ChevronDown, Plus, Calendar, Upload, Image as ImageIcon } from 'lucide-react';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import toast from 'react-hot-toast';
  import apiService from '../services/api';
  import { Review } from '../types';
  import { extractTourText } from '../utils/tourUtils';

type ReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ReviewStatus | 'all'>('PENDING');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [editingStatus, setEditingStatus] = useState<ReviewStatus>('PENDING');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [reviewSources, setReviewSources] = useState<Array<{ id: number; name: string; image?: string }>>([]);
  const [availableTours, setAvailableTours] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [uploadedImages, setUploadedImages] = useState<Array<{ file: File; preview: string; url?: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state for creating review
  const [formData, setFormData] = useState({
    title: '',
    userName: '',
    reviewDate: '',
    helpfulCount: 0,
    reviewSourceId: '',
    tourId: '',
    review: '',
    rating: 5,
    status: 'APPROVED' as ReviewStatus,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [filterTourId, setFilterTourId] = useState<string>('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [reviewIdToAction, setReviewIdToAction] = useState<number | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const rowsPerPage = 10;

  // Get flavourId from localStorage or context
  const getFlavourId = (): string => {
    return apiService.getFlavourId() || localStorage.getItem('flavourId') || '';
  };

  // Fetch all reviews for the selected status. Tour filtering + pagination are
  // done client-side so the tour dropdown sees every review, not just one page.
  const fetchReviews = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        setReviews([]);
        setIsLoading(false);
        return;
      }

      const status = filterStatus === 'all' ? undefined : (filterStatus as ReviewStatus);
      const response = await apiService.getReviews(flavourId, status, 1, 1000);
      setReviews(response.reviews || []);
    } catch (error: any) {
      setError('Failed to load reviews');
      toast.error(error.message || 'Failed to load reviews');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  // Reset to the first page whenever the tour filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTourId]);

  // Load the tour list for the filter dropdown on mount
  useEffect(() => {
    const flavourId = getFlavourId();
    if (!flavourId) return;
    apiService
      .getAvailableToursForReview(flavourId)
      .then(setAvailableTours)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const flavourId = getFlavourId();
        if (!flavourId) return;

        const [sources, tours] = await Promise.all([
          apiService.getReviewSources(),
          apiService.getAvailableToursForReview(flavourId),
        ]);
        setReviewSources(sources);
        setAvailableTours(tours);
      } catch (error) {
        // ignore dropdown load failure
      }
    };

    if (isCreateModalOpen) {
      loadDropdownData();
    }
  }, [isCreateModalOpen]);

  const handleView = (review: Review) => {
    // Open modal immediately with existing review data
    setSelectedReview(review);
    setIsViewModalOpen(true);
  };

  const handleEdit = (review: Review) => {
    setSelectedReview(review);
    setEditingStatus(review.status);
    setIsEditModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedReview) return;

    setIsUpdatingStatus(true);
    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        toast.error('Please create an app first to manage reviews');
        setIsUpdatingStatus(false);
        return;
      }

      await apiService.updateReviewStatus(selectedReview.id, editingStatus, flavourId);
      toast.success(`Review status updated to ${editingStatus} successfully!`);
      setIsEditModalOpen(false);
      setSelectedReview(null);
      await fetchReviews();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update review status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const MAX_IMAGES = 5;
    const remainingSlots = MAX_IMAGES - uploadedImages.length;

    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesArray = Array.from(files);
    const filesToAdd = filesArray.slice(0, remainingSlots);

    if (filesArray.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more image(s) can be added`);
    }

    const newImages: Array<{ file: File; preview: string }> = [];
    const skippedFiles: string[] = [];

    for (const file of filesToAdd) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" is not an image file`);
        continue;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" is too large. File size must be less than 5MB`);
        continue;
      }

      // Check for duplicates by comparing name, size, and last modified date
      const isDuplicate = uploadedImages.some(existing => 
        existing.file.name === file.name &&
        existing.file.size === file.size &&
        existing.file.lastModified === file.lastModified
      );

      if (isDuplicate) {
        skippedFiles.push(file.name);
        continue;
      }

      newImages.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }

    if (skippedFiles.length > 0) {
      toast.error(`Skipped duplicate image(s): ${skippedFiles.join(', ')}`);
    }

    if (newImages.length > 0) {
      setUploadedImages(prev => [...prev, ...newImages]);
      if (newImages.length === filesToAdd.length) {
        toast.success(`${newImages.length} image(s) added successfully`);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleCreateReview = async () => {
    if (!formData.userName || !formData.reviewDate || !formData.tourId || !formData.review) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreatingReview(true);
    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        toast.error('Please create an app first to manage reviews');
        setIsCreatingReview(false);
        return;
      }

      // Extract files from uploadedImages
      const imageFiles = uploadedImages.map(img => img.file);

      await apiService.createReview({
        title: formData.title.trim() || undefined,
        userName: formData.userName,
        reviewDate: formData.reviewDate,
        helpfulCount: formData.helpfulCount,
        reviewSourceId: formData.reviewSourceId ? parseInt(formData.reviewSourceId) : undefined,
        tourId: parseInt(formData.tourId),
        review: formData.review,
        rating: formData.rating,
        status: formData.status,
      }, flavourId, imageFiles.length > 0 ? imageFiles : undefined);

      toast.success('Review created successfully!');
      setIsCreateModalOpen(false);
      setFormData({
        title: '',
        userName: '',
        reviewDate: '',
        helpfulCount: 0,
        reviewSourceId: '',
        tourId: '',
        review: '',
        rating: 5,
        status: 'APPROVED',
      });
      setUploadedImages([]);
      await fetchReviews();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create review');
    } finally {
      setIsCreatingReview(false);
    }
  };

  const handleOpenCreateModal = () => {
    // Set default date to today
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, reviewDate: today }));
    setIsCreateModalOpen(true);
  };

  const handleApproveClick = (reviewId: number) => {
    setReviewIdToAction(reviewId);
    setIsApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!reviewIdToAction) return;

    setIsActionLoading(true);
    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        toast.error('Please create an app first to manage reviews');
        setIsApproveDialogOpen(false);
        setIsActionLoading(false);
        return;
      }
      await apiService.approveReview(reviewIdToAction, flavourId);
      toast.success('Review approved successfully!');
      setIsApproveDialogOpen(false);
      setReviewIdToAction(null);
      await fetchReviews();
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve review');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRejectClick = (reviewId: number) => {
    setReviewIdToAction(reviewId);
    setIsRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!reviewIdToAction) return;

    setIsActionLoading(true);
    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        toast.error('Please create an app first to manage reviews');
        setIsRejectDialogOpen(false);
        setIsActionLoading(false);
        return;
      }
      await apiService.rejectReview(reviewIdToAction, flavourId);
      toast.success('Review rejected successfully!');
      setIsRejectDialogOpen(false);
      setReviewIdToAction(null);
      await fetchReviews();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject review');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDelete = async (reviewId: number) => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }

    try {
      const flavourId = getFlavourId();
      if (!flavourId) {
        toast.error('Please create an app first to manage reviews');
        return;
      }
      await apiService.deleteReview(reviewId, flavourId);
      toast.success('Review deleted successfully!');
      await fetchReviews();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete review');
    }
  };

  const getStatusIcon = (status: ReviewStatus) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: ReviewStatus) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1';
    switch (status) {
      case 'APPROVED':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {getStatusIcon(status)}
            Approved
          </span>
        );
      case 'REJECTED':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            {getStatusIcon(status)}
            Rejected
          </span>
        );
      case 'PENDING':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {getStatusIcon(status)}
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  const columns = [
    {
      key: 'tour' as keyof Review,
      header: 'Tour Name',
      sortable: false,
      render: (value: any, review: Review) => (
        <span className="text-gray-900">
          {review.tour?.name ? extractTourText(review.tour.name) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'userName' as keyof Review,
      header: 'Email/Mobile',
      sortable: false,
      render: (value: any, review: Review) => {
        const emailOrPhone = review.user?.email || review.user?.phone || 'N/A';
        return (
          <span className="text-gray-900">{emailOrPhone}</span>
        );
      },
    },
    {
      key: 'rating' as keyof Review,
      header: 'Rating',
      sortable: false,
      render: (value: any, review: Review) => renderStars(review.rating),
    },
    {
      key: 'status' as keyof Review,
      header: 'Status',
      sortable: false,
      render: (value: any, review: Review) => getStatusBadge(review.status),
    },
    {
      key: 'actions' as keyof Review,
      header: 'Actions',
      sortable: false,
      render: (value: any, review: Review) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(review)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
          {review.status === 'PENDING' && (
            <>
              <button
                onClick={() => handleApproveClick(review.id)}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Approve"
              >
                <CheckCircle className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleRejectClick(review.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Reject"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </>
          )}
          {(review.status === 'APPROVED' || review.status === 'REJECTED') && (
            <button
              onClick={() => handleEdit(review)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Edit Status"
            >
              <Edit className="h-4 w-4" />
            </button>
          )}
          {/* <button
            onClick={() => handleDelete(review.id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button> */}
        </div>
      ),
    },
  ];

  // Client-side tour filter + pagination over the fetched status set
  const filteredReviews = filterTourId
    ? reviews.filter(
        (r) => String((r as any).tour?.id ?? (r as any).tourId) === filterTourId,
      )
    : reviews;
  const total = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const pagedReviews = filteredReviews.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Review Management</h1>
          <p className="text-gray-600">Manage and moderate user reviews</p>
        </div>
        <Button onClick={handleOpenCreateModal} disabled={!getFlavourId()} title={!getFlavourId() ? 'Please create an app first' : ''} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Review
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('PENDING')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === 'PENDING'
                  ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilterStatus('APPROVED')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === 'APPROVED'
                  ? 'bg-green-100 text-green-800 border-2 border-green-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilterStatus('REJECTED')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === 'REJECTED'
                  ? 'bg-red-100 text-red-800 border-2 border-red-500'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rejected
            </button>
          </div>
          <div className="flex items-center gap-2 lg:ml-auto">
            <span className="text-sm font-medium text-gray-700">Filter by Tour:</span>
            <select
              value={filterTourId}
              onChange={(e) => setFilterTourId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-primary focus:border-primary min-w-[180px]"
            >
              <option value="">All Tours</option>
              {availableTours.map((tour) => (
                <option key={tour.id} value={tour.id}>
                  {extractTourText(tour.name)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reviews...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-red-600">{error}</p>
          <Button onClick={() => fetchReviews()} className="mt-4">
            Retry
          </Button>
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-600">No reviews found</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <Table data={pagedReviews} columns={columns} />
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * rowsPerPage + 1} to{' '}
                {Math.min(currentPage * rowsPerPage, total)} of {total} reviews
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {isViewModalOpen && selectedReview && (
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          title="Review Details"
        >
          <div className="space-y-4">
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flower Name
              </label>
              <p className="text-gray-900">{selectedReview.flavour?.name || 'N/A'}</p>
            </div> */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Tour Name
              </label>
              <p className="text-gray-900">{selectedReview.tour?.name ? extractTourText(selectedReview.tour.name) : 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Email/Mobile
              </label>
              <p className="text-gray-900">
                {selectedReview.user?.email || selectedReview.user?.phone || 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Rating
              </label>
              {renderStars(selectedReview.rating)}
            </div>
            {/* <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Review Title
              </label>
              <p className="text-gray-900">{selectedReview.reviewTitle || 'N/A'}</p>
            </div> */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Review
              </label>
              <p className="text-gray-900">{selectedReview.review || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Status
              </label>
              {getStatusBadge(selectedReview.status)}
            </div>
            {selectedReview.imageUrl && selectedReview.imageUrl.length > 0 && (
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Attached Images ({selectedReview.imageUrl.length})
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {selectedReview.imageUrl.slice(0, 5).map((url, index) => (
                    <div
                      key={index}
                      className="relative cursor-pointer group"
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img
                        src={url}
                        alt={`Review image ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-4">
              {selectedReview.status === 'PENDING' && (
                <>
                  <Button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleApproveClick(selectedReview.id);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleRejectClick(selectedReview.id);
                    }}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Image Lightbox Modal */}
      {selectedImageIndex !== null && selectedReview && selectedReview.imageUrl && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Main Image */}
            <div className="relative">
              <img
                src={selectedReview.imageUrl[selectedImageIndex]}
                alt={`Review image ${selectedImageIndex + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />

              {/* Navigation Arrows */}
              {selectedReview.imageUrl.length > 1 && (
                <>
                  {selectedImageIndex > 0 && (
                    <button
                      onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-colors"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                  )}
                  {selectedImageIndex < selectedReview.imageUrl.length - 1 && (
                    <button
                      onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-colors"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  )}
                </>
              )}

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black bg-opacity-50 rounded-full text-white text-sm">
                {selectedImageIndex + 1} / {selectedReview.imageUrl.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            {selectedReview.imageUrl.length > 1 && (
              <div className="mt-4 flex gap-2 justify-center overflow-x-auto pb-2">
                {selectedReview.imageUrl.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === selectedImageIndex
                        ? 'border-blue-500 ring-2 ring-blue-300'
                        : 'border-gray-600 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <Modal
        isOpen={isApproveDialogOpen}
        onClose={() => {
          setIsApproveDialogOpen(false);
          setReviewIdToAction(null);
        }}
        title="Approve Review"
        size="sm"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Are you sure you want to approve this review?
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setIsApproveDialogOpen(false);
                setReviewIdToAction(null);
              }}
              variant="outline"
              disabled={isActionLoading}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              variant="primary"
              disabled={isActionLoading}
              loading={isActionLoading}
              className="min-w-[100px] bg-green-600 hover:bg-green-700"
            >
              Approve
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Confirmation Dialog */}
      <Modal
        isOpen={isRejectDialogOpen}
        onClose={() => {
          setIsRejectDialogOpen(false);
          setReviewIdToAction(null);
        }}
        title="Reject Review"
        size="sm"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="h-12 w-12 text-red-600" />
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Are you sure you want to reject this review?
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setIsRejectDialogOpen(false);
                setReviewIdToAction(null);
              }}
              variant="outline"
              disabled={isActionLoading}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              variant="danger"
              disabled={isActionLoading}
              loading={isActionLoading}
              className="min-w-[100px]"
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Review Status Modal */}
      {isEditModalOpen && selectedReview && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedReview(null);
          }}
          title="Edit Review Status"
          size="md"
        >
          <div className="space-y-4">
            {/* Read-only Review Details */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Tour Name
              </label>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md">
                {selectedReview.tour?.name ? extractTourText(selectedReview.tour.name) : 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Email/Mobile
              </label>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md">
                {selectedReview.user?.email || selectedReview.user?.phone || 'N/A'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Rating
              </label>
              <div className="bg-gray-50 p-2 rounded-md">
                {renderStars(selectedReview.rating)}
              </div>
            </div>
            {/* <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Review Title
              </label>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md">
                {selectedReview.reviewTitle || 'N/A'}
              </p>
            </div> */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1">
                Review
              </label>
              <p className="text-gray-900 bg-gray-50 p-2 rounded-md">
                {selectedReview.review || 'N/A'}
              </p>
            </div>
            {selectedReview.imageUrl && selectedReview.imageUrl.length > 0 && (
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Attached Images ({selectedReview.imageUrl.length})
                </label>
                <div className="grid grid-cols-5 gap-2 bg-gray-50 p-2 rounded-md">
                  {selectedReview.imageUrl.slice(0, 5).map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Review image ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Editable Status Field */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Review Status *
              </label>
              <div className="relative">
                <select
                  value={editingStatus}
                  onChange={(e) => setEditingStatus(e.target.value as ReviewStatus)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer"
                  disabled={isUpdatingStatus}
                >
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Current status: <span className="font-semibold">{selectedReview.status}</span>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setSelectedReview(null);
                }}
                variant="outline"
                disabled={isUpdatingStatus}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStatus}
                variant="primary"
                disabled={isUpdatingStatus || editingStatus === selectedReview.status}
                loading={isUpdatingStatus}
              >
                Update Status
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Review Modal */}
      {isCreateModalOpen && (
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setFormData({
              title: '',
              userName: '',
              reviewDate: '',
              helpfulCount: 0,
              reviewSourceId: '',
              tourId: '',
              review: '',
              rating: 5,
              status: 'APPROVED',
            });
            // Clean up image preview URLs
            uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
            setUploadedImages([]);
          }}
          title="Add Review"
          size="lg"
        >
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Write your review heading here"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                disabled={isCreatingReview}
              />
            </div>

            {/* User Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Name *
              </label>
              <input
                type="text"
                value={formData.userName}
                onChange={(e) => setFormData(prev => ({ ...prev, userName: e.target.value }))}
                placeholder="Enter user name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                disabled={isCreatingReview}
              />
            </div>

            {/* Review Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Date *
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.reviewDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, reviewDate: e.target.value }))}
                  placeholder="dd-mm-yyyy"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  disabled={isCreatingReview}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Helpful Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Helpful Count
              </label>
              <input
                type="number"
                value={formData.helpfulCount}
                onChange={(e) => setFormData(prev => ({ ...prev, helpfulCount: parseInt(e.target.value) || 0 }))}
                placeholder="Enter Helpful Count"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                disabled={isCreatingReview}
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Images
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageUpload(e.target.files)}
                className="hidden"
                disabled={isCreatingReview}
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                disabled={isCreatingReview || uploadedImages.length >= 5}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload Images
                {uploadedImages.length > 0 && `(${uploadedImages.length}/5)`}
              </Button>
              <p className="text-xs text-gray-500 mt-1">
                Maximum 5 images allowed. Each image must be less than 5MB.
              </p>

              {/* Image Preview Grid */}
              {uploadedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-5 gap-2">
                  {uploadedImages.map((imageData, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageData.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        disabled={isCreatingReview}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title="Remove image"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Review Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Source
              </label>
              <div className="relative">
                <select
                  value={formData.reviewSourceId}
                  onChange={(e) => setFormData(prev => ({ ...prev, reviewSourceId: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer"
                  disabled={isCreatingReview}
                >
                  <option value="">Please Select Option</option>
                  {reviewSources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Add Tour */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Add Tour *
              </label>
              <div className="relative">
                <select
                  value={formData.tourId}
                  onChange={(e) => setFormData(prev => ({ ...prev, tourId: e.target.value }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer"
                  disabled={isCreatingReview}
                >
                  <option value="">Please Select Option</option>
                  {availableTours.map(tour => (
                    <option key={tour.id} value={tour.id}>{extractTourText(tour.name)}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Review */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review *
              </label>
              <textarea
                value={formData.review}
                onChange={(e) => setFormData(prev => ({ ...prev, review: e.target.value }))}
                placeholder="Review"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                disabled={isCreatingReview}
              />
            </div>

            {/* Ratings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ratings *
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    disabled={isCreatingReview}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= formData.rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">({formData.rating}/5)</span>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as ReviewStatus }))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer"
                  disabled={isCreatingReview}
                >
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setFormData({
                    title: '',
                    userName: '',
                    reviewDate: '',
                    helpfulCount: 0,
                    reviewSourceId: '',
                    tourId: '',
                    review: '',
                    rating: 5,
                    status: 'APPROVED',
                  });
                  // Clean up image preview URLs
                  uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
                  setUploadedImages([]);
                }}
                variant="outline"
                disabled={isCreatingReview}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateReview}
                variant="primary"
                disabled={isCreatingReview}
                loading={isCreatingReview}
              >
                Add
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Reviews;

